/* eslint-disable no-unused-vars */
/* eslint-disable space-before-function-paren */
import * as actions from './routes.actions';
import * as requests from '../../services/requests/routes/routes';
import { getAgencySignal } from '../../services/network/agencyAbortController';
import {
    completeAgencyScopedRequest,
    createRequestGenerationTracker,
    failAgencyScopedRequest,
    isRequestCanceled,
} from '../../services/network/agencyScopedRequest';
import { getUserAgencyId } from '../../services/requests/request.utils';
import { LOG_EVENT_TYPE, createLogEvent } from 'src/logger/log-event';
import { LOG_LEVEL, logger } from 'src/logger/logger';
import { createTransactionId } from 'src/logger/transaction-id';

const CODE_LOCATION = 'src/routes/routes/routes.operations.js';

const createRoutesOperationEvent = ({
    methodName,
    transactionId,
    level,
    message,
    outcome,
    contentType,
    contentAvailable,
    pageCanContinue,
    failureReason,
    usesMockData,
}) => createLogEvent({
    eventType: LOG_EVENT_TYPE.OPERATION,
    codeLocation: CODE_LOCATION,
    methodName,
    level,
    message,
    outcome,
    transactionId,
    attributes: {
        frontend_data: {
            content_type: contentType,
            content_available: contentAvailable,
            routes_page_can_continue: pageCanContinue,
            ...(failureReason ? { reason: failureReason } : {}),
            ...(usesMockData ? { uses_mock_data: true } : {}),
        },
    },
});

const logRoutesOperationCanceled = ({
    methodName,
    transactionId,
    contentType,
    failureReason = 'agency_context_changed',
}) => {
    logger.event(createRoutesOperationEvent({
        methodName,
        transactionId,
        level: LOG_LEVEL.INFO,
        message: 'Routes data request canceled after context changed',
        outcome: 'canceled',
        contentType,
        contentAvailable: false,
        pageCanContinue: true,
        failureReason,
    }));
};

const logRoutesOperationFailure = ({
    methodName,
    transactionId,
    contentType,
    pageCanContinue,
    failureReason,
    usesMockData,
}) => {
    logger.event(createRoutesOperationEvent({
        methodName,
        transactionId,
        level: pageCanContinue ? LOG_LEVEL.WARN : LOG_LEVEL.ERROR,
        message: pageCanContinue
            ? 'Routes page content degraded'
            : 'Routes page content unavailable',
        outcome: pageCanContinue ? 'degraded' : 'failure',
        contentType,
        contentAvailable: false,
        pageCanContinue,
        failureReason,
        usesMockData,
    }));
};

const logRoutesOperationSuccess = ({
    methodName,
    transactionId,
    contentType,
    usesMockData,
}) => {
    logger.event(createRoutesOperationEvent({
        methodName,
        transactionId,
        level: LOG_LEVEL.INFO,
        message: 'Routes page content available',
        outcome: 'success',
        contentType,
        contentAvailable: true,
        pageCanContinue: true,
        usesMockData,
    }));
};

const completeRoutesOperation = ({
    dispatch,
    signal,
    response,
    successAction,
    errorAction,
    mapSuccess,
    isCurrent,
    methodName,
    transactionId,
    contentType,
    pageCanContinue,
    usesMockData,
}) => {
    const superseded = isCurrent && !isCurrent();
    const canceled = superseded || signal?.aborted || response == null;

    if (canceled) {
        completeAgencyScopedRequest({
            dispatch,
            signal,
            response,
            successAction,
            errorAction,
            mapSuccess,
            isCurrent,
        });
        logRoutesOperationCanceled({
            methodName,
            transactionId,
            contentType,
            failureReason: superseded
                ? 'request_superseded'
                : 'agency_context_changed',
        });
        return;
    }

    try {
        completeAgencyScopedRequest({
            dispatch,
            signal,
            response,
            successAction,
            errorAction,
            mapSuccess,
            isCurrent,
        });
    } catch (error) {
        logRoutesOperationFailure({
            methodName,
            transactionId,
            contentType,
            pageCanContinue,
            failureReason: 'response_processing_failed',
            usesMockData,
        });
        failAgencyScopedRequest({
            dispatch,
            signal,
            error,
            errorAction,
            isCurrent,
        });
        return;
    }

    logRoutesOperationSuccess({
        methodName,
        transactionId,
        contentType,
        usesMockData,
    });
};

const failRoutesOperation = ({
    dispatch,
    signal,
    error,
    errorAction,
    isCurrent,
    methodName,
    transactionId,
    contentType,
    pageCanContinue,
    failureReason = 'service_request_failed',
    usesMockData,
}) => {
    const superseded = isCurrent && !isCurrent();
    const canceled = superseded
        || signal?.aborted
        || isRequestCanceled(error);

    failAgencyScopedRequest({
        dispatch,
        signal,
        error,
        errorAction,
        isCurrent,
    });

    if (canceled) {
        logRoutesOperationCanceled({
            methodName,
            transactionId,
            contentType,
            failureReason: superseded
                ? 'request_superseded'
                : 'agency_context_changed',
        });
        return;
    }

    logRoutesOperationFailure({
        methodName,
        transactionId,
        contentType,
        pageCanContinue,
        failureReason,
        usesMockData,
    });
};

const inFlightRouteStopsRequests = new Set();
const lastRouteStopsRequestAt = new Map();
const ROUTE_STOPS_DEDUPE_WINDOW_MS = 400;

const buildRouteStopsRequestKey = (params = {}) => {
    const agencyId = getUserAgencyId() || 'unknown';
    const route = params.route || 'all';
    const offset = params.offset ?? 'na';
    const limit = params.limit ?? 'na';

    return `${agencyId}:${route}:${offset}:${limit}`;
};

export const getAgencyRoutesOperation
    = () =>
        async (dispatch) => {
            dispatch(actions.getAgencyRoutesRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            try {
                const response = await requests
                    .getAgencyRoutes(signal, { transactionId });
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getAgencyRoutesSuccess,
                    errorAction: actions.getAgencyRoutesError,
                    methodName: 'getAgencyRoutesOperation',
                    transactionId,
                    contentType: 'agency_routes',
                    pageCanContinue: false,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getAgencyRoutesError,
                    methodName: 'getAgencyRoutesOperation',
                    transactionId,
                    contentType: 'agency_routes',
                    pageCanContinue: false,
                });
            }
        };

export const getTLAndOnTimePerformanceOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getTLAndOnTimePerformanceRequest());
            const signal = getAgencySignal();

            try {
                const response = await requests
                    .getTrafficLightsOperationDataAndOnTimePerformanceRequest(params, signal);
                completeAgencyScopedRequest({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getTLAndOnTimePerformanceSuccess,
                    errorAction: actions.getTLAndOnTimePerformanceError,
                });
            } catch(error) {
                failAgencyScopedRequest({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getTLAndOnTimePerformanceError,
                });
            }
        };

export const getTSPActivityOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getTSPActivityRequest());
            const signal = getAgencySignal();

            try {
                const response = await requests
                    .getTSPActivityRequest(params, signal);
                completeAgencyScopedRequest({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getTSPActivitySuccess,
                    errorAction: actions.getTSPActivityError,
                    mapSuccess: (payload) => payload.result,
                });
            } catch(error) {
                failAgencyScopedRequest({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getTSPActivityError,
                });
            }
        };

export const getRoutesStopsOperation
    = (params = {}) =>
        async (dispatch) => {
            const requestKey = buildRouteStopsRequestKey(params);
            const now = Date.now();
            const lastAt = lastRouteStopsRequestAt.get(requestKey) || 0;

            if (inFlightRouteStopsRequests.has(requestKey)) {
                return;
            }

            if (now - lastAt < ROUTE_STOPS_DEDUPE_WINDOW_MS) {
                return;
            }

            inFlightRouteStopsRequests.add(requestKey);
            dispatch(actions.getRoutesStopsRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            try {
                const response = await requests
                    .getRoutesStopsRequest(params, signal, { transactionId });
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getRoutesStopsSuccess,
                    errorAction: actions.getRoutesStopsError,
                    methodName: 'getRoutesStopsOperation',
                    transactionId,
                    contentType: 'route_stops',
                    pageCanContinue: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getRoutesStopsError,
                    methodName: 'getRoutesStopsOperation',
                    transactionId,
                    contentType: 'route_stops',
                    pageCanContinue: true,
                });
            } finally {
                inFlightRouteStopsRequests.delete(requestKey);
                lastRouteStopsRequestAt.set(requestKey, Date.now());
            }
        };

export const getRoutesPerformanceOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getRoutesPerformanceRequest());
            const signal = getAgencySignal();

            try {
                const response = await requests
                    .getRoutePerformanceRequest(params, signal);
                completeAgencyScopedRequest({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getRoutesPerformanceSuccess,
                    errorAction: actions.getRoutesPerformanceError,
                    mapSuccess: (payload) => payload.result,
                });
            } catch(error) {
                failAgencyScopedRequest({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getRoutesPerformanceError,
                });
            }
        };

export const getPriorityRequestsOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getPriorityRequestsRequest());
            const signal = getAgencySignal();
            const mockData = [
                {
                    vehicle_id: '1984',
                    intersection: 'Powel & Milwaukie',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '7189',
                    intersection: 'SE Division Pl & SE 8th Ave',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '2718',
                    intersection: 'NW Division St & NW Angeline Ave',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '3681',
                    intersection: 'SE Division St & SE 174th Ave',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '1093',
                    intersection: 'SE Division St & NW Eastwood Ave ',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '1984',
                    intersection: 'Powel & Milwaukie',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '7189',
                    intersection: 'SE Division Pl & SE 8th Ave',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '2718',
                    intersection: 'NW Division St & NW Angeline Ave',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '3681',
                    intersection: 'SE Division St & SE 174th Ave',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '1093',
                    intersection: 'SE Division St & NW Eastwood Ave ',
                    date: '2021-08-02 8:12:19 AM',
                },
                {
                    vehicle_id: '3681',
                    intersection: 'SE Division St & SE 170th Ave',
                    date: '2021-08-02 8:12:19 AM',
                },
            ];

            try {
                const response = await requests
                    .getRouteTSPRequestsRequest(params, signal);
                completeAgencyScopedRequest({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getPriorityRequestsSuccess,
                    errorAction: actions.getPriorityRequestsError,
                    mapSuccess: () => mockData,
                });
            } catch(error) {
                failAgencyScopedRequest({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getPriorityRequestsError,
                });
            }
        };

const routeDetailsTracker = createRequestGenerationTracker();

export const getRouteDetailsOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getRouteDetailsRequest());
            const signal = getAgencySignal();
            const gen = routeDetailsTracker.next();
            const isCurrent = () => routeDetailsTracker.isCurrent(gen);
            const transactionId = createTransactionId();

            try {
                const response = await requests
                    .getRouteDetailsRequest(params, signal, { transactionId });
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getRouteDetailsSuccess,
                    errorAction: actions.getRouteDetailsError,
                    isCurrent,
                    methodName: 'getRouteDetailsOperation',
                    transactionId,
                    contentType: 'route_details',
                    pageCanContinue: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getRouteDetailsError,
                    isCurrent,
                    methodName: 'getRouteDetailsOperation',
                    transactionId,
                    contentType: 'route_details',
                    pageCanContinue: true,
                });
            }
        };

export const getHeadwayOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getHeadwayRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            try {
                const response = await requests // replace mockData below with response
                    .getHeadwayRequest(params, signal, { transactionId });
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getHeadwaySuccess,
                    errorAction: actions.getHeadwayError,
                    methodName: 'getHeadwayOperation',
                    transactionId,
                    contentType: 'headway_chart',
                    pageCanContinue: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getHeadwayError,
                    methodName: 'getHeadwayOperation',
                    transactionId,
                    contentType: 'headway_chart',
                    pageCanContinue: true,
                });
            }
        };

export const getRoutePerformanceByStopOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getRoutePerformanceByStopRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            try {
                const response = await requests
                    .getRoutePerformanceByStopRequest(
                        params,
                        signal,
                        { transactionId },
                    );
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getRoutePerformanceByStopSuccess,
                    errorAction: actions.getRoutePerformanceByStopError,
                    methodName: 'getRoutePerformanceByStopOperation',
                    transactionId,
                    contentType: 'route_performance_by_stop_chart',
                    pageCanContinue: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getRoutePerformanceByStopError,
                    methodName: 'getRoutePerformanceByStopOperation',
                    transactionId,
                    contentType: 'route_performance_by_stop_chart',
                    pageCanContinue: true,
                });
            }
        };

export const getTravelTimeOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getTravelTimeRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            try {
                const response = await requests // replace mockData below with response
                    .getTravelTimeRequest(params, signal, { transactionId });
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getTravelTimeSuccess,
                    errorAction: actions.getTravelTimeError,
                    methodName: 'getTravelTimeOperation',
                    transactionId,
                    contentType: 'travel_time_chart',
                    pageCanContinue: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getTravelTimeError,
                    methodName: 'getTravelTimeOperation',
                    transactionId,
                    contentType: 'travel_time_chart',
                    pageCanContinue: true,
                });
            }
        };

export const getRedLightWaitTimeOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getRedLightWaitTimeRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            const mockData = [
                { x: 14, y: 0 },
                { x: 2, y: 1 },
                { x: 9, y: 2 },
                { x: 3, y: 3 },
                { x: 2, y: 4 },
                { x: 7, y: 5 },
                { x: 16, y: 6 },
                { x: 12, y: 7 },
                { x: 16, y: 8 },
            ];

            try {
                const response = await requests // replace mockData below with response
                    .getRedLightWaitTimeRequest(
                        params,
                        signal,
                        { transactionId },
                    );
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getRedLightWaitTimeSuccess,
                    errorAction: actions.getRedLightWaitTimeError,
                    mapSuccess: () => mockData,
                    methodName: 'getRedLightWaitTimeOperation',
                    transactionId,
                    contentType: 'red_light_wait_time_chart',
                    pageCanContinue: true,
                    usesMockData: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getRedLightWaitTimeError,
                    methodName: 'getRedLightWaitTimeOperation',
                    transactionId,
                    contentType: 'red_light_wait_time_chart',
                    pageCanContinue: true,
                    usesMockData: true,
                });
            }
        };

export const getBusStopDwellTimeOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getBusStopDwellTimeRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            try {
                const response = await requests
                    .getBusStopDwellTimeRequest(
                        params,
                        signal,
                        { transactionId },
                    );
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getBusStopDwellTimeSuccess,
                    errorAction: actions.getBusStopDwellTimeError,
                    methodName: 'getBusStopDwellTimeOperation',
                    transactionId,
                    contentType: 'bus_stop_dwell_time_chart',
                    pageCanContinue: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getBusStopDwellTimeError,
                    methodName: 'getBusStopDwellTimeOperation',
                    transactionId,
                    contentType: 'bus_stop_dwell_time_chart',
                    pageCanContinue: true,
                });
            }
        };

export const getSegmentTravelTimeOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getSegmentTravelTimeRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            try {
                const response = await requests // replace mockData below with response
                    .getSegmentTravelTimeRequest(
                        params,
                        signal,
                        { transactionId },
                    );
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getSegmentTravelTimeSuccess,
                    errorAction: actions.getSegmentTravelTimeError,
                    methodName: 'getSegmentTravelTimeOperation',
                    transactionId,
                    contentType: 'segment_travel_time_chart',
                    pageCanContinue: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getSegmentTravelTimeError,
                    methodName: 'getSegmentTravelTimeOperation',
                    transactionId,
                    contentType: 'segment_travel_time_chart',
                    pageCanContinue: true,
                    failureReason: 'service_or_response_processing_failed',
                });
            }
        };

export const getAverageVehicleSpeedOperation
    = (params = {}) =>
        async (dispatch) => {
            dispatch(actions.getAverageVehicleSpeedRequest());
            const signal = getAgencySignal();
            const transactionId = createTransactionId();

            try {
                const response = await requests
                    .getAverageVehicleSpeedRequest(
                        params,
                        signal,
                        { transactionId },
                    );
                completeRoutesOperation({
                    dispatch,
                    signal,
                    response,
                    successAction: actions.getAverageVehicleSpeedSuccess,
                    errorAction: actions.getAverageVehicleSpeedError,
                    methodName: 'getAverageVehicleSpeedOperation',
                    transactionId,
                    contentType: 'average_vehicle_speed_chart',
                    pageCanContinue: true,
                });
            } catch(error) {
                failRoutesOperation({
                    dispatch,
                    signal,
                    error,
                    errorAction: actions.getAverageVehicleSpeedError,
                    methodName: 'getAverageVehicleSpeedOperation',
                    transactionId,
                    contentType: 'average_vehicle_speed_chart',
                    pageCanContinue: true,
                });
            }
        };
